/**
 * Recurring slice — in-memory list of recurring definitions.
 *
 * Mutations match `addCategory` / `updateCategory` style: simple list with
 * id-keyed updates. The catch-up algorithm itself lives in
 * `src/lib/recurring/scheduler.ts` (pure, no zustand).
 */

import type { RecurringRow } from '@/lib/sheets/types';

export interface RecurringState {
  items: RecurringRow[];
}

export interface RecurringActions {
  setItems: (items: RecurringRow[]) => void;
  addDef: (row: RecurringRow) => void;
  updateDef: (id: string, patch: Partial<RecurringRow>) => void;
  removeDef: (id: string) => void;
}

export type RecurringSlice = RecurringState & RecurringActions;

export const initialRecurringState: RecurringState = {
  items: [],
};

export const createRecurringSlice =
  <T extends RecurringSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): RecurringSlice => ({
    ...initialRecurringState,

    setItems: (items) => set({ items } as Partial<T>),

    addDef: (row) =>
      set((state) => ({ items: [...state.items, row] } as Partial<T>)),

    updateDef: (id, patch) =>
      set((state) => ({
        items: state.items.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      } as Partial<T>)),

    removeDef: (id) =>
      set((state) => ({
        items: state.items.filter((r) => r.id !== id),
      } as Partial<T>)),
  });
